/**
 * 009 — Sección de álbum compartido dentro del render público (US1 subir + US3 ver).
 * Se monta en el slot de "Comparte tus fotos" de las plantillas. Móvil-first.
 * Privacidad: todo por access_code (RPC). Sin autoría para los invitados (C1/FR-011).
 */
import { useRef, useState, type CSSProperties } from 'react';
import { toast } from 'sonner';
import { Button, PhotoGallery, Ornament } from '@/invitation/ds';
import { useAlbumPhotos, useUploadGuestPhoto, useRecordUpgradeIntent } from '@/hooks/useGuestAlbum';

interface GuestAlbumSectionProps {
  accessCode: string;
  invitationId: string;
  /** Copy "aún no disponible" del fake-door tras registrar la intención. */
  autoApprove?: boolean;
}

const flourish: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-flourish, var(--font-script))',
  color: 'var(--accent)',
  lineHeight: 1.1,
};

export default function GuestAlbumSection({ accessCode, invitationId, autoApprove = true }: GuestAlbumSectionProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [intentSent, setIntentSent] = useState(false);
  const { data: photos = [] } = useAlbumPhotos(accessCode);
  const upload = useUploadGuestPhoto(accessCode, invitationId);
  const upgrade = useRecordUpgradeIntent(accessCode);

  // Sin código válido no hay álbum (privacidad, SC-003): ni grid ni subida.
  if (!accessCode || !invitationId) return null;

  const handlePick = () => inputRef.current?.click();

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // permite re-seleccionar los mismos archivos
    if (files.length === 0) return;

    let ok = 0;
    let replaced = 0;
    let pending = 0;
    let firstError: string | null = null;

    // Secuencial: respeta el orden del tope/reemplazo y evita carreras en el RPC.
    for (const file of files) {
      try {
        const res = await upload.mutateAsync({ file });
        ok += 1;
        if (res.replaced_url) replaced += 1;
        else if (!res.photo.is_approved) pending += 1;
      } catch (err) {
        if (!firstError) {
          firstError =
            err instanceof Error
              ? err.message
              : typeof err === 'object' && err && 'message' in err
                ? String((err as { message: unknown }).message)
                : 'No se pudo subir la foto.';
        }
      }
    }

    if (ok > 0) {
      const parts: string[] = [`${ok} foto${ok > 1 ? 's' : ''} subida${ok > 1 ? 's' : ''}`];
      if (pending > 0) parts.push('pendiente' + (pending > 1 ? 's' : '') + ' de aprobación');
      if (replaced > 0) parts.push(`${replaced} reemplazó al tope de 10`);
      toast.success('¡Gracias! ' + parts.join(' · '));
    }
    if (firstError) {
      toast.error(files.length > 1 ? `Algunas fotos no se subieron: ${firstError}` : firstError);
    }
  };

  const handleUpgrade = async () => {
    try {
      await upgrade.mutateAsync();
    } catch {
      /* registrar la intención es best-effort; no bloquea al invitado */
    }
    setIntentSent(true);
  };

  return (
    <div style={{ maxWidth: 1000, margin: 'clamp(24px,3vw,44px) auto 0', display: 'grid', gap: 'clamp(18px,2.2vw,30px)', justifyItems: 'center', textAlign: 'center' }}>
      <p style={{ ...flourish, fontSize: 'clamp(30px,3.4vw,46px)' }}>Álbum de la fiesta</p>
      <Ornament variant="flourish" width={180} />

      {photos.length > 0 ? (
        <PhotoGallery items={photos.map((ph) => ({ src: ph.photo_url }))} columns={2} />
      ) : (
        <p style={{ margin: 0, fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 'clamp(16px,1.6vw,22px)', color: 'var(--fg-secondary)' }}>
          Aún no hay fotos. ¡Sé el primero en compartir un recuerdo!
        </p>
      )}

      <input ref={inputRef} type="file" accept="image/*,.heic,.heif" multiple hidden onChange={handleFiles} />
      <div style={{ display: 'grid', gap: 12, justifyItems: 'center' }}>
        <Button variant="outline" size="md" onClick={handlePick} disabled={upload.isPending}>
          {upload.isPending ? 'Subiendo…' : 'Subir mis fotos'}
        </Button>
        {!autoApprove && (
          <span style={{ fontFamily: 'var(--font-caps)', fontSize: 'clamp(10px,.9vw,12px)', letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--fg-secondary)', opacity: 0.8 }}>
            Las fotos se muestran tras la aprobación de los novios
          </span>
        )}
      </div>

      {/* Fake-door del upsell (§10): registra intención, no cobra ni habilita nada. */}
      {intentSent ? (
        <p style={{ margin: 0, fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 'clamp(14px,1.4vw,18px)', color: 'var(--fg-secondary)' }}>
          ¡Gracias por tu interés! Subir fotos ilimitadas aún no está disponible; te avisaremos pronto.
        </p>
      ) : (
        <button
          type="button"
          onClick={handleUpgrade}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontFamily: 'var(--font-body)', fontSize: 'clamp(13px,1.3vw,16px)', textDecoration: 'underline', textUnderlineOffset: 3 }}
        >
          ¿Quieres subir más de 10 fotos? Subir ilimitadas (pagar)
        </button>
      )}
    </div>
  );
}
